import ReactGA from 'react-ga4';

/**
 * Initialize Google Analytics
 * Should be called once when the app starts
 */
export const initGA = (): void => {
  const measurementId = import.meta.env.GA_MEASUREMENT_ID;
  
  if (measurementId && measurementId.trim() !== '') {
    try {
      ReactGA.initialize(measurementId, {
        gaOptions: {
          anonymizeIp: true, // Anonymize IP for privacy
        },
      });
      console.log('Google Analytics initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
    }
  } else {
    console.warn('Google Analytics Measurement ID not found. Analytics will not be tracked.');
  }
};

/**
 * Track page views
 * @param path - The page path to track
 */
export const trackPageView = (path: string): void => {
  if (import.meta.env.GA_MEASUREMENT_ID) {
    ReactGA.send({ hitType: 'pageview', page: path });
  }
};

/**
 * Track custom events
 * @param category - Event category
 * @param action - Event action
 * @param label - Optional event label
 * @param value - Optional numeric value
 */
export const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number
): void => {
  if (import.meta.env.GA_MEASUREMENT_ID) {
    ReactGA.event({
      category,
      action,
      label,
      value,
    });
  }
};

/**
 * Track file upload events
 * @param fileSize - Size of the uploaded file in bytes
 * @param fileName - Name of the uploaded file
 */
export const trackFileUpload = (fileSize: number, fileName: string): void => {
  trackEvent('File', 'Upload', fileName, fileSize);
};

/**
 * Track file parse events
 * @param success - Whether the parse was successful
 * @param recordCount - Number of records parsed
 */
export const trackFileParse = (success: boolean, recordCount?: number): void => {
  trackEvent(
    'File',
    success ? 'Parse Success' : 'Parse Error',
    undefined,
    recordCount
  );
};

/**
 * Track export events
 * @param format - Export format (CSV, JSON, Excel, PDF)
 * @param recordCount - Number of records exported
 */
export const trackExport = (format: string, recordCount?: number): void => {
  trackEvent('Export', 'Download', format, recordCount);
};

/**
 * Track theme toggle events
 * @param theme - The new theme ('light' or 'dark')
 */
export const trackThemeToggle = (theme: string): void => {
  trackEvent('UI', 'Theme Toggle', theme);
};

/**
 * Track view changes
 * @param viewName - Name of the view (Summary, Batches, Entries, Validation)
 */
export const trackViewChange = (viewName: string): void => {
  trackEvent('Navigation', 'View Change', viewName);
};

/**
 * Track errors
 * @param errorMessage - The error message
 * @param errorLocation - Where the error occurred
 */
export const trackError = (errorMessage: string, errorLocation: string): void => {
  trackEvent('Error', errorLocation, errorMessage);
};

